import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { EnvelopeOpen, PaperPlaneTilt, Trash, Pencil, ArrowsClockwise, CheckCircle, Spinner, User, Lightning } from "@phosphor-icons/react";
import { PageHeader, EmptyState } from "../components/ui/kaizen";

const INTENT_LABELS = {
  essai_gratuit: { label: "Cours d'essai", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
  info_tarifs: { label: "Tarifs", color: "bg-sky-500/10 text-sky-400 border-sky-500/30" },
  info_horaires: { label: "Horaires", color: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  inscription_directe: { label: "Inscription !", color: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
  autre: { label: "Autre", color: "bg-foreground/10 text-foreground/70 border-foreground/30" },
};

export default function ProspectsModule() {
  const [drafts, setDrafts] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/prospects/drafts?status=${filter}`);
      setDrafts(r.data.drafts || []);
    } catch {
      toast.error("Chargement impossible");
    }
  };
  useEffect(() => { load(); }, [filter]);

  const triggerGeneration = async () => {
    setGenerating(true);
    try {
      await api.post("/prospects/drafts/generate");
      toast.success("Génération lancée. Recharge dans 2-3 min.");
      setTimeout(load, 30000);
    } catch {
      toast.error("Échec");
    }
    setGenerating(false);
  };

  const sendDraft = async (message_id) => {
    setBusy(message_id);
    try {
      await api.post(`/prospects/drafts/${message_id}/approve-and-send`);
      toast.success("Courriel envoyé !");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec envoi");
    }
    setBusy(null);
  };

  const deleteDraft = async (message_id) => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    try {
      await api.delete(`/prospects/drafts/${message_id}`);
      toast.success("Supprimé");
      await load();
    } catch {
      toast.error("Échec");
    }
  };

  const saveEdit = async (message_id) => {
    setBusy(message_id);
    try {
      await api.put(`/prospects/drafts/${message_id}`, {
        draft_subject: editing.draft_subject,
        draft_body: editing.draft_body,
      });
      toast.success("Modifié");
      setEditing(null);
      await load();
    } catch {
      toast.error("Échec");
    }
    setBusy(null);
  };

  return (
    <div className="p-6 md:p-8 space-y-6 text-zinc-100" data-testid="prospects-module">
      <PageHeader
        title="Prospects · Brouillons Gmail"
        icon={Lightning}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Prospects Gmail" }]}
        subtitle="Réponses générées par l'IA (français, ton accueillant). Approuve, modifie ou supprime avant envoi. Chaque brouillon vise à amener le prospect en cours d'essai (19,98 $) au dojo."
        actions={
          <button
            data-testid="btn-generate-drafts"
            onClick={triggerGeneration}
            disabled={generating}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-semibold flex items-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
          >
            {generating ? <Spinner className="animate-spin" size={14}/> : <ArrowsClockwise size={14} weight="bold"/>}
            {generating ? "En cours…" : "Générer 30 brouillons"}
          </button>
        }
      />

      {/* Tabs status */}
      <div className="flex gap-2 hard-border-b">
        {[
          { id: "pending", label: "À approuver" },
          { id: "sent", label: "Envoyés" },
          { id: "", label: "Tous" },
        ].map(t => {
          const active = filter === t.id;
          return (
            <button
              key={t.id || "all"}
              data-testid={`tab-${t.id || "all"}`}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${active ? "border-[#D90429] text-[#D90429]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {drafts.length === 0 && (
        <EmptyState
          icon={EnvelopeOpen}
          title={filter === "pending" ? "Aucun brouillon en attente" : "Rien à afficher"}
          description={filter === "pending" ? "Clique sur « Générer » pour analyser les courriels non lus et préparer des réponses." : "Change de filtre pour voir d'autres brouillons."}
        />
      )}

      <div className="space-y-3" data-testid="drafts-list">
        {drafts.map(d => {
          const intent = INTENT_LABELS[d.intent_detected] || INTENT_LABELS.autre;
          const isEditing = editing?.message_id === d.message_id;
          return (
            <div key={d.message_id} className="hard-border" data-testid="draft-card">
              {/* Header */}
              <div className="p-4 flex items-start gap-3 flex-wrap border-b border-foreground/10">
                <div className="w-10 h-10 hard-border bg-foreground text-background flex items-center justify-center font-bold shrink-0">
                  {d.first_name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="truncate">{d.from_name_full}</strong>
                    <span className={`text-[10px] px-2 py-0.5 border ${intent.color}`}>{intent.label}</span>
                    {d.status === "sent" && <span className="text-[10px] px-2 py-0.5 bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 flex items-center gap-1"><CheckCircle weight="fill" size={10}/> Envoyé</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Reçu le {d.original_date?.slice(0, 25)}
                  </div>
                  <div className="text-sm font-medium mt-1">{d.original_subject}</div>
                  <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">{d.original_snippet}</div>
                </div>
              </div>

              {/* Brouillon */}
              <div className="p-4 bg-secondary/10">
                {isEditing ? (
                  <div className="space-y-3">
                    <input
                      data-testid="input-edit-subject"
                      type="text"
                      value={editing.draft_subject}
                      onChange={(e) => setEditing({ ...editing, draft_subject: e.target.value })}
                      className="w-full px-3 py-2 hard-border text-sm bg-background"
                    />
                    <textarea
                      data-testid="input-edit-body"
                      value={editing.draft_body}
                      onChange={(e) => setEditing({ ...editing, draft_body: e.target.value })}
                      rows={10}
                      className="w-full px-3 py-2 hard-border text-sm bg-background font-mono"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(d.message_id)} disabled={busy === d.message_id} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold hard-border hover:opacity-90 disabled:opacity-50">Sauvegarder</button>
                      <button onClick={() => setEditing(null)} className="px-3 py-1.5 hard-border text-xs hover:bg-muted">Annuler</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-muted-foreground mb-1">Brouillon de réponse :</div>
                    <div className="font-semibold mb-2">{d.draft_subject}</div>
                    <div className="text-sm whitespace-pre-wrap">{d.draft_body}</div>
                    {d.next_action && (
                      <div className="text-xs text-muted-foreground mt-3 italic">💡 Suggestion: {d.next_action}</div>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              {!isEditing && d.status === "pending" && (
                <div className="p-3 flex gap-2 flex-wrap border-t border-foreground/10">
                  <button
                    data-testid="btn-approve-send"
                    onClick={() => sendDraft(d.message_id)}
                    disabled={busy === d.message_id}
                    className="px-3 py-1.5 bg-[#D90429] text-white text-sm font-semibold hard-border hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <PaperPlaneTilt weight="bold" size={14} />
                    {busy === d.message_id ? "Envoi…" : "Approuver et envoyer"}
                  </button>
                  <button
                    data-testid="btn-edit"
                    onClick={() => setEditing({ message_id: d.message_id, draft_subject: d.draft_subject, draft_body: d.draft_body })}
                    className="px-3 py-1.5 hard-border text-sm hover:bg-muted flex items-center gap-1.5"
                  >
                    <Pencil size={14} />
                    Modifier
                  </button>
                  <button
                    data-testid="btn-delete"
                    onClick={() => deleteDraft(d.message_id)}
                    className="px-3 py-1.5 hard-border text-sm hover:bg-destructive hover:text-destructive-foreground flex items-center gap-1.5"
                  >
                    <Trash size={14} />
                    Supprimer
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
